<script lang="ts">
	/**
	 * `ConfirmButton`'s sibling for actions that need a reason, not just a yes.
	 *
	 * Same two-step shape — click the styled trigger, answer in a small dialog — but
	 * the dialog carries a single text input and `confirm` is dispatched with its
	 * value. Reach for it when the audit trail needs the "why" (rejection notes,
	 * halt reasons) rather than adding a bespoke modal per call site.
	 */

	import { Button, Input, Modal } from 'flowbite-svelte';
	import { createEventDispatcher } from 'svelte';
	import { toastWarning } from '../stores/toast';

	/** Noun phrase naming what is being typed, e.g. `rejection notes`. */
	export let action: string = 'proceed';

	/**
	 * Instruction shown above the input, overriding the one built from `action`.
	 *
	 * Undefined by default so it tracks `action` reactively — a default assignment
	 * would freeze the text to whatever `action` was at mount.
	 */
	export let confirmText: string | undefined = undefined;
	$: resolvedConfirmText = confirmText ?? `Enter ${action} below.`;

	/**
	 * The consequences, under the headline rather than run on after it.
	 *
	 * `confirmText` is what is about to happen — a short line naming the thing and the amount — and
	 * this is what it costs. Run together they make one long sentence that is read as a block and
	 * therefore not read at all, which on a dialog whose whole purpose is a considered yes is the
	 * one outcome worth avoiding. Optional: a prompt with nothing to warn about renders no second
	 * line rather than an empty one.
	 */
	export let confirmDetail: string = '';

	/** Label of the dialog's accept button. Name the actual verb ("Reject"). */
	export let confirmButtonText: string = 'Confirm';

	export let disabled: boolean | undefined = false;
	export let pill: boolean | undefined = false;
	export let outline: boolean | undefined = false;
	export let size: 'xs' | 'sm' | 'lg' | 'xl' | 'md' | undefined = 'md';
	export let color: 'red' | 'yellow' | 'green' | 'purple' | 'blue' | 'light' | 'dark' | 'primary' | 'none' | 'alternative' | undefined = undefined;

	/** Font Awesome solid icon name shown in the dialog, without the `fa-` prefix. */
	export let icon: string = 'keyboard';

	let className: string = '';
	export { className as class };

	export let inputPlaceholder: string = 'Enter value';
	/** Two-way bindable current input text. Reset on every open. */
	export let inputValue: string = '';

	/**
	 * Gates the accept button. Pass `() => true` to make the note optional —
	 * that is the difference between "approve (notes welcome)" and
	 * "reject (reason required)".
	 */
	export let inputValidator: (value: string) => boolean = (value: string) => value.trim().length > 0;

	/**
	 * Two-way bindable so a parent can pre-open the prompt (e.g. an email deep link
	 * that lands the reviewer on a pre-staged Approve/Reject dialog).
	 */
	export let open: boolean = false;

	const dispatcher = createEventDispatcher();
</script>

<Modal bind:open size="xs">
	<div class="text-center">
		<i class="fa-solid fa-{icon} fa-3x mb-4" />
		<!--
			The headline carries the weight of the page's own text colour; the detail under it is
			secondary and reads as such. They were one paragraph in the muted grey, which made the
			thing being decided and the consequences of deciding it the same size and the same
			colour — so the amount, which is the one part somebody checks before saying yes, had
			nothing to distinguish it.
		-->
		<h3 class="text-lg font-medium text-gray-900 dark:text-white">{resolvedConfirmText}</h3>
		{#if confirmDetail.length > 0}
			<p class="mt-2 text-sm text-gray-500 dark:text-gray-400">{confirmDetail}</p>
		{/if}

		<Input type="text" placeholder={inputPlaceholder} class="mb-4 mt-5 w-full" bind:value={inputValue} />

		<div class="flex justify-between items-center">
			<Button on:click={() => (open = false)} color="alternative">Cancel</Button>
			<Button
				disabled={!inputValidator(inputValue)}
				on:click={() => {
					// Belt-and-braces: the button is already disabled when invalid, but a
					// keyboard submit or a validator that changed under us must not slip through.
					if (!inputValidator(inputValue)) {
						toastWarning('Please enter a value.');
						return false;
					}

					open = false;
					dispatcher('confirm', inputValue);
				}}
				color="red"
				>{confirmButtonText}
			</Button>
		</div>
	</div>
</Modal>

<!-- Clearing `inputValue` on open stops the previous answer leaking into the next one. -->
<Button {disabled} class={className} {pill} {outline} {size} {color} on:click={() => ((inputValue = ''), (open = true))}>
	<slot />
</Button>
